/**
 * Chat group management via existing REST endpoints
 * (backend/src/routes/chatGroup.routes.js). Firestore mirrors are synced
 * server-side; the sidebar's membership listener picks changes up live.
 */
import { useCallback } from 'react';
import { api } from '@/lib/apiClient';
import type { ChatGroup } from '@flock-types/chat.types';

export interface ChatGroupMemberDetail {
  userId: string;
  name: string;
  email: string;
  profileImageUrl: string | null;
  joinedAt: string | null;
  isOwner: boolean;
}

export function useChatGroupActions(groupId: string | null) {
  const requireGroup = useCallback(() => {
    if (!groupId) throw new Error('No group selected');
    return groupId;
  }, [groupId]);

  const createChatGroup = useCallback(
    async (data: { name: string; description?: string; memberIds?: string[] }) => {
      const gid = requireGroup();
      const response = await api.post<{ success: boolean; data: ChatGroup }>(
        `/groups/${gid}/chat-groups`,
        data
      );
      return response.data;
    },
    [requireGroup]
  );

  const createDM = useCallback(
    async (targetUserId: string): Promise<{ chatGroup: ChatGroup; isNew: boolean }> => {
      const gid = requireGroup();
      const response = await api.post<{ success: boolean; data: ChatGroup; isNew: boolean }>(
        `/groups/${gid}/dm`,
        { targetUserId }
      );
      return { chatGroup: response.data, isNew: response.isNew };
    },
    [requireGroup]
  );

  const listMembers = useCallback(
    async (chatGroupId: string): Promise<ChatGroupMemberDetail[]> => {
      const gid = requireGroup();
      const response = await api.get<{ success: boolean; data: ChatGroupMemberDetail[] }>(
        `/groups/${gid}/chat-groups/${chatGroupId}/members`
      );
      return response.data;
    },
    [requireGroup]
  );

  const addMembers = useCallback(
    async (chatGroupId: string, userIds: string[]) => {
      const gid = requireGroup();
      await api.post(`/groups/${gid}/chat-groups/${chatGroupId}/members`, { userIds });
    },
    [requireGroup]
  );

  const removeMember = useCallback(
    async (chatGroupId: string, userId: string) => {
      const gid = requireGroup();
      await api.delete(`/groups/${gid}/chat-groups/${chatGroupId}/members/${userId}`);
    },
    [requireGroup]
  );

  const leaveChatGroup = useCallback(
    async (chatGroupId: string) => {
      const gid = requireGroup();
      await api.post(`/groups/${gid}/chat-groups/${chatGroupId}/leave`);
    },
    [requireGroup]
  );

  const archiveChatGroup = useCallback(
    async (chatGroupId: string) => {
      const gid = requireGroup();
      await api.post(`/groups/${gid}/chat-groups/${chatGroupId}/archive`);
    },
    [requireGroup]
  );

  return {
    createChatGroup,
    createDM,
    listMembers,
    addMembers,
    removeMember,
    leaveChatGroup,
    archiveChatGroup,
  };
}

export default useChatGroupActions;
